import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Alert,
  Avatar,
  Button,
  Drawer,
  IconButton,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import React, { useState, useEffect } from "react";
import DataStatusAlert from "../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import FtdDrawer from "../ForeCasterDrawer/FtdDrawer";
import { Box } from "@mui/system";
import GrowthDrawer from "../ForeCasterDrawer/GrowthDrawer";
import ProfitDrawer from "../ForeCasterDrawer/ProfitDrawer";
import { useSelector, useDispatch } from "react-redux";
import {
  setSelectedDuplicateForecast,
  setSelectedMetaSharedUid,
  setSelectedForecasterContent,
} from "../../../../../features/forecastersSlice";
import {
  formatDate,
  getTemplateType,
} from "../../../../../utils/ForcasterUtils";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { selectUserData } from "../../../../../features/userSlice";
import PeriodMoreInfoModal from "./PeriodMoreInfoModal";

const ForeCasterTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedFocus, setSelectedFocus] = useState("");
  const [moreInfoModalOpen, setMoreInfoModalOpen] = useState(false);
  const forecasters = useSelector(
    (state: any) => state.forecasters?.forecastersList,
  );
  const selectedForecastStatus = useSelector(
    (state: any) => state?.forecasters?.selectedForecastStatus,
  );
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [sort, setSort] = useState<{
    keytoSort: string | undefined;
    direction: "asc" | "desc" | undefined;
  }>({ keytoSort: "created", direction: "desc" });

  const filteredData = forecasters?.filter((forecasters: any) => {
    if (!selectedForecastStatus) return true;
    return forecasters?.status === selectedForecastStatus;
  });

  // Modified handleChangePage
  const handleChangePage = (_event: unknown, newPage: number) => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredData.length / rowsPerPage) - 1,
    );
    const validPage = Math.min(maxPage, Math.max(0, newPage));
    setPage(validPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage); // Reset to first page when changing rows per page
    setPage(0);
  };

  // Reset URL page parameter when filtered data changes
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line
  }, [selectedForecastStatus]);

  const [open, setOpen] = useState(false);

  const toggleDrawer = (newOpen: boolean, focus: string) => () => {
    setOpen(newOpen);
    if (newOpen) {
      setSelectedFocus(focus);
    }
  };

  const tableheaders = [
    { id: 0, KEY: "version", label: "Version", sortable: true },
    { id: 1, KEY: "template_type", label: "Prediction Focus", sortable: true },
    { id: 2, KEY: "status", label: "Status", sortable: true },
    { id: 3, KEY: "notes", label: "Note", sortable: true },
    { id: 4, KEY: "created", label: "Created On", sortable: true },
    { id: 5, KEY: "created_by", label: "Created By", sortable: true },
    { id: 6, KEY: "quick_actions", label: "Quick Actions", sortable: false },
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    setSort({
      keytoSort: header.KEY,
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;
        if (keytoSort === undefined) {
          return 0;
        }
        let aValue, bValue;

        if (keytoSort === "month") {
          // Parse the month and year for sorting
          const [monthA, yearA] = a[keytoSort].split("/").map(Number);
          const [monthB, yearB] = b[keytoSort].split("/").map(Number);

          // Create comparable values: year first, then month
          aValue = yearA * 100 + monthA; // e.g., 202301 for January 2023
          bValue = yearB * 100 + monthB; // e.g., 202301 for January 2023
        } else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort],
  );

  const sortedRows = getSortedData(filteredData || []);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedForecast = sortedRows?.slice(startIndex, endIndex);
  const response = useSelector(selectUserData);
  const hasCreateForcastersPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "create_forecasters",
  );

  const ForeCasterDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const handleClickOnDuplicate = (row: any) => {
    const updatedParams = new URLSearchParams(location.search);
    updatedParams.set("metaSharedUid", row?.meta_shared_uid);
    dispatch(setSelectedDuplicateForecast(row));
    dispatch(setSelectedMetaSharedUid(row?.meta_shared_uid));
    navigate(`DuplicateForecast?${updatedParams.toString()}`);
  };

  const handleClickOnTemplateType = (row: any) => {
    dispatch(setSelectedForecasterContent(row));
    dispatch(setSelectedMetaSharedUid(row?.meta_shared_uid));
  };

  const DrawerList = (
    <Box
      sx={{ width: 260, padding: "16px 15px" }}
      role="presentation"
      onClick={ForeCasterDrawer(false)}
    >
      {selectedFocus === "FTD" && <FtdDrawer />}
      {selectedFocus === "Growth" && <GrowthDrawer />}
      {selectedFocus === "Profit" && <ProfitDrawer />}
    </Box>
  );
  
  const handleMoreInfoClick = () => {
    setMoreInfoModalOpen(true)
  };
  
  const getCurrentPeriod = () => {
    const now = new Date();
    const day = now.getDate();
    const month = now.getMonth() + 1; // 1–12
  
    if (month === 1 && day <= 15) return "NO_RESTRICTION";
    if ((month === 1 && day >= 16) || month === 2 || month === 3 || (month === 4 && day <= 15))
      return "JAN_MAR_LOCKED";
    if ((month === 4 && day >= 16) || month === 5 || month === 6 || (month === 7 && day <= 15))
      return "JAN_JUN_LOCKED";
    if ((month === 7 && day >= 16) || month === 8 || month === 9 || (month === 10 && day <= 15))
      return "JAN_SEP_LOCKED";
  
    return "ALL_LOCKED";
  };
  
  const getRestrictionText = () => {
    const period = getCurrentPeriod();
    switch (period) {
      case "NO_RESTRICTION":
        return "No Restrictions would be applied until January 15";
      case "JAN_MAR_LOCKED":
        return "You can edit the next three quarters (Q2 – Q4) until April 15";
      case "JAN_JUN_LOCKED":
        return "You can edit the next two quarters (Q3 – Q4) until July 15";
      case "JAN_SEP_LOCKED":
        return "You can edit the quarters (Q4) until October 15";
      case "ALL_LOCKED":
        return "No Editing for this period is permitted since  October 16";
      default:
        return "";
    }
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Alert
          severity="info" 
          sx={{
            flex: 1,
            alignItems: "center",
            backgroundColor: "transparent",
            borderRadius: "4px",
            border: "none",
            borderColor: "divider",
            boxShadow: "none",
            padding: "0",
          }}
        >
          <Typography fontWeight={500} fontSize={16} sx={{color: "info.main"}}>
            Please note: Editing & creating availability depends on the current calendar
            period.
          </Typography>
          <Typography fontWeight={500} fontSize={13} sx={{color: "info.main"}}>
            <Box component="span">
              {getRestrictionText()}
            </Box>
            <Box component="span" sx={{ marginLeft: "15px" }}>
              <span
                onClick={handleMoreInfoClick}
                style={{
                  color: "#01579B",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                MORE INFO
              </span>
            </Box>
          </Typography>
        </Alert>
        {hasCreateForcastersPermission ? (
          <Button variant="contained" color="secondary" data-tracker="129">
            <RouterLink
              to={`Createnewforecast?${(() => {
                const params = new URLSearchParams(searchParams);
                // Remove predictionFocus parameter if it exists
                if (params.has("predictionFocus")) {
                  params.delete("predictionFocus");
                }
                return params.toString();
              })()}`}
              style={{ textDecoration: "none", color: "unset" }}
            >
              <Stack
                direction="row"
                alignItems={"initial"}
                justifyContent={"center"}
                gap={1}
              >
                Create New forecast <AddOutlinedIcon fontSize="small" />
              </Stack>
            </RouterLink>
          </Button>
        ) : (
          <Button
            disabled
            variant="contained"
            color="secondary"
            data-tracker="129"
          >
            <Stack
              direction="row"
              alignItems={"initial"}
              justifyContent={"center"}
              gap={1}
            >
              Create New forecast <AddOutlinedIcon fontSize="small" />
            </Stack>
          </Button>
        )}
      </Stack>
      <TableContainer className="PageScantablewrap" sx={{ marginTop: "16px" }}>
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders?.map((header: any, _index: any) => (
                <TableCell key={header.id}>
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedForecast.length > 0 && paginatedForecast ? (
              paginatedForecast?.map((row: any, index: any) => {
                // Check if forecaster_content is empty or doesn't exist
                const isContentEmpty =
                  !row?.forecaster_content ||
                  row.forecaster_content.length === 0;

                return (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>{row?.version ? row?.version : ""}</TableCell>

                      {/* View / Prediction Focus Column */}
                      <TableCell data-tracker="126">
                        {isContentEmpty ? (
                          <Tooltip
                            title="forecaster content is not available"
                            arrow
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.disabled",
                                cursor: "not-allowed",
                                display: "inline-block",
                              }}
                            >
                              {getTemplateType(row?.template_type)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <RouterLink
                            data-tracker="126"
                            to={`forecasterInfo?${(() => {
                              const params = new URLSearchParams(searchParams);
                              params.set("page", "0");
                              params.set("metaSharedUid", row?.meta_shared_uid);
                              if (Number(row?.template_type) === 1) {
                                params.set("templateType", "FTD");
                              } else if (Number(row?.template_type) === 2) {
                                params.set("templateType", "Growth");
                              } else if (Number(row?.template_type) === 3) {
                                params.set("templateType", "Profit");
                              }
                              return params.toString();
                            })()}`}
                            style={{
                              textDecoration: "underline",
                              color: "#9C27B0",
                            }}
                            onClick={() => handleClickOnTemplateType(row)}
                          >
                            {getTemplateType(row?.template_type)}
                          </RouterLink>
                        )}
                      </TableCell>

                      <TableCell>
                        <DataStatusAlert
                          status={row?.status ? row?.status : ""}
                        />
                      </TableCell>
                      <TableCell>{row?.notes ? row?.notes : ""}</TableCell>
                      <TableCell>
                        {row?.created ? formatDate(row?.created) : ""}
                      </TableCell>
                      <TableCell>
                        <Stack
                          flexDirection={"row"}
                          alignItems={"center"}
                          gap={1}
                        >
                          <Avatar
                            alt={row?.created_by ? row?.created_by : ""}
                            src="#"
                            sx={{ width: 24, height: 24 }}
                          />
                          {row?.created_by ? row?.created_by : ""}
                        </Stack>
                      </TableCell>

                      {/* Quick Actions Column */}
                      <TableCell>
                        <Tooltip title="Info" placement="top">
                          <IconButton
                            sx={{ padding: "0 16px 0 0" }}
                            disableRipple
                            data-tracker="128"
                            onClick={toggleDrawer(
                              true,
                              getTemplateType(row?.template_type),
                            )}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              color="secondary"
                            />
                          </IconButton>
                        </Tooltip>

                        {/* Duplicate Icon with empty content check */}
                        <Tooltip
                          title={
                            isContentEmpty
                              ? "forecaster content is not available"
                              : "Copy"
                          }
                          placement="top"
                        >
                          <span>
                            <IconButton
                              sx={{ padding: "0 16px 0 0" }}
                              data-tracker="127"
                              onClick={() => handleClickOnDuplicate(row)}
                              disableRipple
                              disabled={
                                isContentEmpty || !hasCreateForcastersPermission
                              }
                            >
                              <ContentCopyOutlinedIcon
                                fontSize="small"
                                color={
                                  isContentEmpty ||
                                  !hasCreateForcastersPermission
                                    ? "disabled"
                                    : "secondary"
                                }
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            ) : (
              // ... No Data Available Row ...
              <TableRow
                sx={{
                  "& > *": {
                    borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
                  },
                }}
              >
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1">No Data Available</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredData?.length / rowsPerPage)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_event, value) => handleChangePage(null, value - 1)}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
        <Drawer
          open={open}
          onClose={toggleDrawer(false, "")}
          anchor="right"
          sx={{ zIndex: 1300 }}
        >
          {DrawerList}
        </Drawer>
      </TableContainer>
      <PeriodMoreInfoModal open={moreInfoModalOpen} setMoreInfoModalOpen={setMoreInfoModalOpen} />
    </>
  );
};

export default ForeCasterTable;
